// GuidedSegmentsPanel.tsx — the Segmentation card's Guided mode body. Each
// segment row has its colour, an editable name, its seed count and (once
// applied) its splat count; "Seed" makes taps on the model add points to
// that segment, and "Box" arms drawing that segment's 3D box (drag across
// the table around the object; then drag its handles to fit, × removes it).
// The Exclude row makes taps mark regions that belong to no segment.
// Tapping an existing point on the model removes it. State and
// the Apply call live in useGuidedSegments.
import Button from 'react-bootstrap/Button';
import { BsArrowCounterclockwise, BsBoundingBox, BsCursor, BsPlusLg, BsTrash, BsX, BsXCircle } from 'react-icons/bs';
import { ui } from 'common';
import { EXCLUDE_COLOR, MAX_SEGMENTS, segmentColor } from './guide';
import type { GuidedSegments } from './useGuidedSegments';

const { Text } = ui;

function Swatch({ color }: { color: string }) {
  return <span aria-hidden className="d-inline-block rounded-circle flex-shrink-0" style={{ width: '0.8rem', height: '0.8rem', background: color }} />;
}

export function GuidedSegmentsPanel({ g, disabled }: { g: GuidedSegments; disabled: boolean }) {
  const counts = new Map((g.result?.segments ?? []).map((s) => [s.id, s.count]));
  const excludeCount = g.draft.exclude?.length ?? 0;
  const isTarget = (id: number | 'exclude') =>
    id === 'exclude' ? g.target.kind === 'exclude' : g.target.kind === 'segment' && g.target.id === id;

  return (
    <div className="d-flex flex-column gap-2">
      <div className="small text-body-secondary">
        Tap the model to drop points on each object, or draw a box around it. Segments grow from their points over the surface, split where
        the colour changes and never leave their box; a box with no points takes everything inside it. Exclude points keep a region out of
        every segment.
      </div>
      {g.draft.segments.map((s, i) => (
        <div key={s.id} className="d-flex align-items-center gap-2 small">
          <Swatch color={segmentColor(i)} />
          <Text value={s.name} onChange={(e) => g.rename(s.id, e.target.value)} aria-label={`Segment ${s.id} name`} style={{ flex: '1 1 0', minWidth: 0, width: 'auto', padding: '0.25rem 0.5rem' }} />
          <span className="text-nowrap text-body-secondary" title="points · splats in the applied segmentation">
            {s.seeds.length} pt{s.box ? ' + box' : ''}
            {counts.has(s.id) ? ` · ${(counts.get(s.id) ?? 0).toLocaleString()}` : ''}
          </span>
          <Button
            size="sm"
            variant={isTarget(s.id) ? 'secondary' : 'outline-secondary'}
            aria-pressed={isTarget(s.id)}
            onClick={() => g.setTarget({ kind: 'segment', id: s.id })}
            title={`Taps add points to ${s.name || 'this segment'}`}
          >
            <BsCursor className="me-1" />
            Seed
          </Button>
          <Button
            size="sm"
            variant={g.drawingBox === s.id ? 'secondary' : 'outline-secondary'}
            aria-pressed={g.drawingBox === s.id}
            onClick={() => g.drawBox(s.id)}
            title={s.box ? `Draw a new box for ${s.name || 'this segment'}` : `Draw a box around ${s.name || 'this segment'}: drag across the table`}
          >
            <BsBoundingBox className="me-1" />
            Box
          </Button>
          {s.box && (
            <Button size="sm" variant="outline-secondary" onClick={() => g.removeBox(s.id)} aria-label={`Remove the ${s.name} box`} title="Remove this box">
              <BsX />
            </Button>
          )}
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={g.draft.segments.length <= 1}
            onClick={() => g.remove(s.id)}
            aria-label={`Delete segment ${s.name}`}
            title="Delete this segment"
          >
            <BsTrash />
          </Button>
        </div>
      ))}
      <div className="d-flex align-items-center gap-2 small">
        <Swatch color={EXCLUDE_COLOR} />
        <span className="flex-grow-1">Exclude</span>
        <span className="text-nowrap text-body-secondary">
          {excludeCount} pt{g.result && g.result.excluded ? ` · ${g.result.excluded.toLocaleString()}` : ''}
        </span>
        <Button
          size="sm"
          variant={isTarget('exclude') ? 'secondary' : 'outline-secondary'}
          aria-pressed={isTarget('exclude')}
          onClick={() => g.setTarget({ kind: 'exclude' })}
          title="Taps mark regions that belong to no segment"
        >
          <BsXCircle className="me-1" />
          Exclude
        </Button>
      </div>
      <div className="d-flex flex-wrap gap-1">
        <Button size="sm" variant="outline-secondary" disabled={g.draft.segments.length >= MAX_SEGMENTS} onClick={g.add}>
          <BsPlusLg className="me-1" />
          Add segment
        </Button>
        <Button size="sm" variant="outline-secondary" onClick={g.undo}>
          <BsArrowCounterclockwise className="me-1" />
          Undo point
        </Button>
        <Button size="sm" variant="outline-secondary" onClick={g.clear}>
          <BsXCircle className="me-1" />
          Clear points
        </Button>
        {g.dirty && g.result && (
          <Button size="sm" variant="outline-secondary" onClick={g.revert} disabled={disabled}>
            <BsArrowCounterclockwise className="me-1" />
            Revert to applied
          </Button>
        )}
      </div>
      {g.notice && <div className="small">{g.notice}</div>}
      {g.problem && <div className="small text-body-secondary">{g.problem}</div>}
      {(g.result?.unmatched.length ?? 0) > 0 && !g.dirty && (
        <div className="small text-body-secondary">
          {g.result?.unmatched.length} applied point(s) no longer land on the model (it was re-refined); move them and apply again.
        </div>
      )}
    </div>
  );
}
